// ListingsPage.jsx



import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Pagination,
  IconButton,
} from "@mui/material";
import { Favorite, FavoriteBorder } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import useFetchListings from "../../hooks/useFetchListings";
import useFavorites from "../../hooks/useFavorites";
import Header from "../header/Header";
import { styles } from "./ListingsPageStyles";
import AdvancedSearch from "./AdvancedSearch";

/**
 * ListingsPage – Shows paginated property listings with advanced search & favourites.
 * Enhanced for SEO using react-helmet-async (meta tags, canonical, JSON‑LD).
 */
const ListingsPage = () => {
  const { listings, loading } = useFetchListings();
  const [search, setSearch] = useState({
    address: "",
    city: "",
    zipCode: "",
    minPrice: "",
    maxPrice: "",
  });
  const [filteredListings, setFilteredListings] = useState([]);
  const { addFavorite, removeFavorite, isFavorite } = useFavorites();
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const listingsPerPage = 10;

  /* ---------------------------------------------------------
   * Filtering logic
   * -------------------------------------------------------*/
  useEffect(() => {
    let filtered = listings;

    if (search.address) {
      filtered = filtered.filter((l) =>
        l.address?.toLowerCase().includes(search.address.toLowerCase())
      );
    }

    if (search.city) {
      filtered = filtered.filter((l) =>
        l.city?.toLowerCase().includes(search.city.toLowerCase())
      );
    }

    if (search.zipCode) {
      filtered = filtered.filter((l) =>
        l.zipCode?.toString().includes(search.zipCode)
      );
    }

    if (search.minPrice || search.maxPrice) {
      const min = parseFloat(search.minPrice) || 0;
      const max = parseFloat(search.maxPrice) || Infinity;
      filtered = filtered.filter((l) => {
        const price = Number(l.price);
        return price >= min && price <= max;
      });
    }

    setFilteredListings(filtered);
    setCurrentPage(1);
  }, [listings, search]);

  /* ---------------------------------------------------------
   * Pagination helpers
   * -------------------------------------------------------*/
  const handlePageChange = (_, value) => setCurrentPage(value);
  const indexOfLast = currentPage * listingsPerPage;
  const indexOfFirst = indexOfLast - listingsPerPage;
  const currentListings = filteredListings.slice(indexOfFirst, indexOfLast);

  /* ---------------------------------------------------------
   * Navigation helpers
   * -------------------------------------------------------*/
  const handleListingClick = (id) => navigate(`/listing/${id}`);
  const toggleFavorite = (id, title, imageUrl) => {
    isFavorite(id) ? removeFavorite(id) : addFavorite(id, title, imageUrl);
  };

  /* ---------------------------------------------------------
   * SEO – dynamic meta & structured data
   * -------------------------------------------------------*/
  const metaTitle = useMemo(() => {
    const city = search.city ? ` in ${search.city}` : "";
    const page = currentPage > 1 ? ` – Page ${currentPage}` : "";
    return `Property Listings${city}${page} | Tenant Tours`;
  }, [search.city, currentPage]);

  const metaDescription = useMemo(() => {
    const count = filteredListings.length;
    const priceMin = search.minPrice || Math.min(...filteredListings.map((l) => Number(l.price) || Infinity));
    const priceMax = search.maxPrice || Math.max(...filteredListings.map((l) => Number(l.price) || 0));
    const city = search.city ? `${search.city} ` : "";
    return `Explore ${count} rental properties ${city}from $${priceMin} – $${priceMax} with photos, prices and details.`;
  }, [filteredListings, search.minPrice, search.maxPrice, search.city]);

  const canonical = `${window.location.origin}${location.pathname}`;

  const structuredData = useMemo(() => {
    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: metaTitle,
      description: metaDescription,
      mainEntity: currentListings.map((l) => ({
        "@type": "Residence",
        name: l.title,
        description: l.description?.slice(0, 160),
        image: l.images?.[0],
        address: {
          "@type": "PostalAddress",
          streetAddress: l.address,
          addressLocality: l.city,
          postalCode: l.zipCode,
        },
        offers: {
          "@type": "Offer",
          price: l.price,
          priceCurrency: "USD",
          url: `${window.location.origin}/listing/${l.id}`,
        },
      })),
    };
  }, [currentListings, metaTitle, metaDescription]);

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <>
      {/* ------------------- SEO Helmet Tags ------------------- */}
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index,follow" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Header />

      <Box component="main" sx={{ pb: 6 }}>
        {/* Search Section */}
        <Box component="section" sx={styles.searchSection}>
          <Typography variant="h4" component="h2" sx={styles.searchHeading}>
            Advanced Search
          </Typography>
          <AdvancedSearch search={search} setSearch={setSearch} />
        </Box>

        {/* Listings Section */}
        <Box component="section" sx={styles.listingsSection}>
          <Typography variant="h1" component="h1" sx={styles.listingsHeading}>
            Property Listings
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {currentListings.length > 0 ? (
              currentListings.map((listing) => (
                <Grid item xs={12} sm={6} md={4} key={listing.id}>
                  <Paper
                    elevation={6}
                    sx={styles.paper}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleListingClick(listing.id)}
                    aria-label={`View details for ${listing.title}`}
                  >
                    <img
                      src={listing.images?.[0] || "/placeholder.jpg"}
                      alt={`${listing.title} – ${listing.address || "Property image"}`}
                      style={styles.image}
                      loading="lazy"
                      width={360}
                      height={240}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1 }}>
                        {listing.title  || listing.address} </Typography>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(listing.id, listing.title, listing.images?.[0]);
                        }}
                        color={isFavorite(listing.id) ? "error" : "primary"}
                        aria-label={isFavorite(listing.id) ? "Remove from favourites" : "Add to favourites"}
                      >
                        {isFavorite(listing.id) ? <Favorite /> : <FavoriteBorder />}
                      </IconButton>
                    </Box>
                    <Typography variant="body1" color="textSecondary">
                      {listing.location || "Location not specified"}
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                      ${Number(listing.price).toLocaleString()} / month
                    </Typography>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>
                No listings found matching your criteria.
              </Typography>
            )}
          </Grid>

          {filteredListings.length > listingsPerPage && (
            <Box sx={styles.pagination}>
              <Pagination
                count={Math.ceil(filteredListings.length / listingsPerPage)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                variant="outlined"
                aria-label="pagination"
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ListingsPage;
